import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import { toast } from "../components/index.js";
import { listPasskeys } from "../api/passkeys.js";
import { webauthnSupported, passkeyFailureMessage } from "../api/webauthn.js";
import {
  requestStepUpOtp,
  confirmStepUpOtp,
  verifyWithPasskey,
  type StepUpAction,
} from "../api/stepup.js";
import { humanError } from "../api/errors.js";

/** Why each action asks, shown above the proof controls. */
const REASONS: Record<StepUpAction, string> = {
  create_api_key:
    "An API key gives long-lived access to your account. Confirm it's you before you create one.",
  export_account:
    "The export contains your personal details and sign-in history. Confirm it's you before you download it.",
  delete_account: "Deleting your account cannot be undone. Confirm it's you first.",
  delete_team: "Deleting a team cannot be undone. Confirm it's you first.",
};

/**
 * stepup-dialog is the "Verify it's you" gate for the actions the server
 * guards with a step-up grant (api/stepup.ts): a passkey assertion when the
 * account has a passkey, or an emailed one-time code. It is controlled like
 * bn-modal: the parent sets `open` and listens for `verified` (detail
 * `{verification}`, the single-use grant bound to `action` and `target`) and
 * `close` (the user cancelled). On `verified` the parent closes the dialog and
 * spends the grant at once; it is valid for five minutes.
 *
 * page-api-keys still runs its own copy of this flow, which came first.
 */
@customElement("stepup-dialog")
export class StepUpDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property() action: StepUpAction = "create_api_key";
  /** The resource the grant is bound to: the team id for delete_team, "" for the rest. */
  @property() target = "";

  // The OTP id once "Email me a code" ran (empty until then), the typed code,
  // and one in-flight flag spanning the passkey ceremony and both OTP round
  // trips. hasPasskeys resolves lazily on first open (null = unknown) so the
  // passkey button only shows when it can actually work.
  @state() private otpId = "";
  @state() private code = "";
  @state() private busy = false;
  @state() private hasPasskeys: boolean | null = null;

  static override styles = css`
    .modal-copy {
      margin: 0 0 var(--bino-space-md);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .verify-passkey {
      margin-bottom: var(--bino-space-md);
    }
  `;

  override willUpdate(changed: PropertyValues) {
    // Codes and grants are single-use: anything left from an earlier round
    // could only produce otp_invalid, so every open starts clean.
    if (changed.has("open") && this.open) {
      this.otpId = "";
      this.code = "";
      void this.ensurePasskeyInfo();
    }
  }

  /**
   * ensurePasskeyInfo resolves whether the passkey button is worth showing:
   * WebAuthn support plus at least one registered credential. A failed lookup
   * stays null so the next open retries; meanwhile only email is offered.
   */
  private async ensurePasskeyInfo() {
    if (this.hasPasskeys !== null) return;
    if (!webauthnSupported()) {
      this.hasPasskeys = false;
      return;
    }
    try {
      this.hasPasskeys = (await listPasskeys()).length > 0;
    } catch {
      // Left null on purpose, see above.
    }
  }

  /** The target as the verify routes want it: absent for untargeted actions. */
  private get wireTarget(): string | undefined {
    return this.target === "" ? undefined : this.target;
  }

  private cancel() {
    if (this.busy) return;
    this.dispatchEvent(new CustomEvent("close"));
  }

  private verified(verification: string) {
    this.dispatchEvent(new CustomEvent("verified", { detail: { verification } }));
  }

  private async usePasskey() {
    if (this.busy) return;
    this.busy = true;
    try {
      this.verified(await verifyWithPasskey(this.action, this.wireTarget));
    } catch (err) {
      // A dismissed authenticator prompt is a cancel: stay open so the user
      // can retry or fall back to email, and say nothing.
      const msg = passkeyFailureMessage(err);
      if (msg) toast(msg, "error");
    } finally {
      this.busy = false;
    }
  }

  private async sendCode() {
    if (this.busy) return;
    this.busy = true;
    try {
      const { otpId } = await requestStepUpOtp();
      this.otpId = otpId;
      this.code = "";
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.busy = false;
    }
  }

  private async confirmCode() {
    const code = this.code.trim();
    if (code === "" || this.otpId === "" || this.busy) return;
    this.busy = true;
    try {
      this.verified(await confirmStepUpOtp(this.otpId, code, this.action, this.wireTarget));
    } catch (e) {
      // otp_invalid covers wrong, expired and foreign codes alike; stay open
      // so the user can retype, or cancel and reopen for a fresh code.
      toast(humanError(e), "error");
    } finally {
      this.busy = false;
    }
  }

  override render() {
    const sent = this.otpId !== "";
    // bn-modal's own close event is composed and would reach the parent
    // directly; stop it here so a cancel mid-ceremony is refused in one place.
    return html`<bn-modal
      ?open=${this.open}
      heading="Verify it's you"
      @close=${(e: Event) => {
        e.stopPropagation();
        this.cancel();
      }}
    >
      <p class="modal-copy">
        ${sent
          ? "We sent a one-time code to your email."
          : REASONS[this.action]}
      </p>
      ${this.hasPasskeys
        ? html`<div class="verify-passkey">
            <bn-button
              variant="secondary"
              block
              ?disabled=${this.busy}
              @click=${() => void this.usePasskey()}
              >Use a passkey</bn-button
            >
          </div>`
        : nothing}
      ${sent
        ? html`<bn-field label="One-time code" required>
            <bn-input
              mono
              inputmode="numeric"
              autocomplete="one-time-code"
              placeholder="123456"
              ?disabled=${this.busy}
              .value=${this.code}
              @input=${(e: CustomEvent) => (this.code = (e.detail as { value: string }).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void this.confirmCode();
                }
              }}
            ></bn-input>
          </bn-field>`
        : nothing}
      <bn-button slot="footer" variant="ghost" @click=${() => this.cancel()}>Cancel</bn-button>
      ${sent
        ? html`<bn-button
            slot="footer"
            variant="primary"
            ?loading=${this.busy}
            ?disabled=${this.code.trim() === ""}
            @click=${() => void this.confirmCode()}
            >Verify</bn-button
          >`
        : html`<bn-button
            slot="footer"
            variant="primary"
            ?loading=${this.busy}
            @click=${() => void this.sendCode()}
            >Email me a code</bn-button
          >`}
    </bn-modal>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "stepup-dialog": StepUpDialog;
  }
}
